/**
 * Detail tables under the import verdict line (SPEC-AUDITOR-CSV-IMPORT.md
 * section 7): added / updated (old -> new) / skipped / flagged only. Unchanged
 * rows are a count and are never listed. Every section hides itself when empty,
 * so a clean re-import of an already-imported month shows no tables at all.
 *
 * v1 exports carry no currency column (spec section 2); the server resolves
 * each row's currency from the mapped shop and sends it per row, so a non-PGK
 * shop previews under its own symbol.
 */
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';

import type { ImportFigures, ImportFlaggedRow, ImportPlainRow, ImportRows, ImportUpdatedRow } from '../../api/index.ts';
import { useDisplayCurrency } from '../../currency/CurrencyContext.tsx';
import { MONEY_NUMERIC_SX, palette } from '../../theme.ts';
import { humanizeReason } from './importSummary.ts';

/** Defensive only: the server resolves a currency for every row (spec section 3
 *  falls back to PGK itself when a shop has never synced one). */
const FALLBACK_CURRENCY = 'PGK';

function SectionHeading({ text }: Readonly<{ text: string }>): React.JSX.Element {
  return (
    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
      {text}
    </Typography>
  );
}

function Section({ title, children }: Readonly<{ title: string; children: React.ReactNode }>): React.JSX.Element {
  return (
    <Stack spacing={1}>
      <SectionHeading text={title} />
      <Paper variant="outlined">
        <TableContainer>
          <Table size="small">{children}</Table>
        </TableContainer>
      </Paper>
    </Stack>
  );
}

function FigureCells({ figures, currency }: Readonly<{ figures: ImportFigures; currency: string }>): React.JSX.Element {
  const { format } = useDisplayCurrency();
  return (
    <>
      <TableCell align="right" sx={MONEY_NUMERIC_SX}>
        {format(figures.sales, currency)}
      </TableCell>
      <TableCell align="right" sx={MONEY_NUMERIC_SX}>
        {format(figures.payout, currency)}
      </TableCell>
      <TableCell align="right" sx={MONEY_NUMERIC_SX}>
        {format(figures.tax, currency)}
      </TableCell>
    </>
  );
}

/** "K40.00 -> K45.00"; the old figure stays present but recedes. The arrow sits
 *  at textSecondary, not textMuted -- text.disabled is only 2.68:1 in light. */
function ChangeCell({
  oldMinor,
  newMinor,
  currency,
}: Readonly<{ oldMinor: number; newMinor: number; currency: string }>): React.JSX.Element {
  const { format } = useDisplayCurrency();
  return (
    <TableCell align="right" sx={MONEY_NUMERIC_SX}>
      <Typography component="span" variant="body2" sx={{ color: palette.textSecondary }}>
        {format(oldMinor, currency)}
      </Typography>
      <Typography component="span" variant="body2" sx={{ px: 0.5, color: palette.textSecondary }}>
        →
      </Typography>
      <Typography component="span" variant="body2">
        {format(newMinor, currency)}
      </Typography>
    </TableCell>
  );
}

function FigureHead(): React.JSX.Element {
  return (
    <TableHead>
      <TableRow>
        <TableCell>Shop</TableCell>
        <TableCell>Date</TableCell>
        <TableCell align="right">Gross Sales</TableCell>
        <TableCell align="right">Payout</TableCell>
        <TableCell align="right">Tax</TableCell>
      </TableRow>
    </TableHead>
  );
}

function PlainRows({ rows }: Readonly<{ rows: ImportPlainRow[] }>): React.JSX.Element {
  return (
    <TableBody>
      {rows.map((row) => (
        <TableRow key={`${row.shop}-${row.date}`}>
          <TableCell>{row.shop}</TableCell>
          <TableCell>{row.date}</TableCell>
          <FigureCells figures={row} currency={row.currency || FALLBACK_CURRENCY} />
        </TableRow>
      ))}
    </TableBody>
  );
}

function UpdatedRows({ rows }: Readonly<{ rows: ImportUpdatedRow[] }>): React.JSX.Element {
  return (
    <TableBody>
      {rows.map((row) => {
        const currency = row.currency || FALLBACK_CURRENCY;
        return (
          <TableRow key={`${row.shop}-${row.date}`}>
            <TableCell>{row.shop}</TableCell>
            <TableCell>{row.date}</TableCell>
            <ChangeCell oldMinor={row.old.sales} newMinor={row.new.sales} currency={currency} />
            <ChangeCell oldMinor={row.old.payout} newMinor={row.new.payout} currency={currency} />
            <ChangeCell oldMinor={row.old.tax} newMinor={row.new.tax} currency={currency} />
          </TableRow>
        );
      })}
    </TableBody>
  );
}

function FlaggedRows({ rows }: Readonly<{ rows: ImportFlaggedRow[] }>): React.JSX.Element {
  return (
    <>
      <TableHead>
        <TableRow>
          <TableCell>Line</TableCell>
          <TableCell>Shop</TableCell>
          <TableCell>Date</TableCell>
          <TableCell>Reason</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((row) => (
          <TableRow key={row.line}>
            <TableCell>{row.line}</TableCell>
            <TableCell>{row.shop ?? '-'}</TableCell>
            <TableCell>{row.date ?? '-'}</TableCell>
            <TableCell>{humanizeReason(row.reason)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </>
  );
}

export interface ImportPreviewTablesProps {
  rows: ImportRows;
}

export function ImportPreviewTables({ rows }: Readonly<ImportPreviewTablesProps>): React.JSX.Element {
  return (
    <Stack spacing={2.5}>
      {rows.added.length > 0 && (
        <Section title={`New rows (${rows.added.length})`}>
          <FigureHead />
          <PlainRows rows={rows.added} />
        </Section>
      )}
      {rows.updated.length > 0 && (
        <Section title={`Updated rows (${rows.updated.length})`}>
          <FigureHead />
          <UpdatedRows rows={rows.updated} />
        </Section>
      )}
      {rows.skippedSync.length > 0 && (
        <Section title={`Skipped — this shop already syncs to v2 (${rows.skippedSync.length})`}>
          <FigureHead />
          <PlainRows rows={rows.skippedSync} />
        </Section>
      )}
      {rows.flagged.length > 0 && (
        <Section title={`Need attention — not imported (${rows.flagged.length})`}>
          <FlaggedRows rows={rows.flagged} />
        </Section>
      )}
    </Stack>
  );
}

export default ImportPreviewTables;
